import React, { useCallback, useEffect, useState } from "react";

import { Alert, AlertTitle, Snackbar } from "@mui/material";

import {
    NotificationPayload,
    NotificationSeverity,
    onNotification,
} from "./notificationEmitter";

interface QueuedNotification extends NotificationPayload {
    id: number;
}

const SEVERITY_MAP: Record<string, NotificationSeverity> = {
    success: "success",
    error: "error",
    warning: "warning",
    info: "info",
};

const toSeverity = (icon?: string): NotificationSeverity =>
    SEVERITY_MAP[icon ?? ""] ?? "info";

let counter = 0;

const NotificationSnackbar: React.FC = () => {
    const [queue, setQueue] = useState<QueuedNotification[]>([]);
    const [current, setCurrent] = useState<QueuedNotification | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const unsub = onNotification((payload) => {
            setQueue((q) => [...q, { ...payload, id: ++counter }]);
        });
        return unsub;
    }, []);

    useEffect(() => {
        if (!open && queue.length > 0) {
            setCurrent(queue[0]);
            setQueue((q) => q.slice(1));
            setOpen(true);
        }
    }, [queue, open]);

    const handleClose = useCallback(
        (_: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === "clickaway") return;
            setOpen(false);
        },
        [],
    );

    if (!current) return null;

    const severity = toSeverity(current.icon);
    const duration = current.timer ?? 4000;

    return (
        <Snackbar
            open={open}
            autoHideDuration={duration}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            TransitionProps={{ onExited: () => setCurrent(null) }}
        >
            <Alert
                onClose={handleClose}
                severity={severity}
                variant="standard"
                sx={{ minWidth: 280, maxWidth: 420 }}
            >
                {current.title && <AlertTitle>{current.title}</AlertTitle>}
                {current.message}
            </Alert>
        </Snackbar>
    );
};

export default NotificationSnackbar;
